import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  archiveEnrichmentJob,
  type ArchiveEnrichmentJobPayload,
} from '../services/enrichment-job-archive.service';

interface ArchiveEnrichmentJobArgs {
  jobId: string;
  payload: ArchiveEnrichmentJobPayload;
}

/**
 * Mutation that archives an enrichment job (`POST jobs/{jobId}/archive`).
 * Invalidates the job-status and catalog-operations caches on success so the
 * job stops showing as active.
 */
export function useArchiveEnrichmentJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, payload }: ArchiveEnrichmentJobArgs) =>
      archiveEnrichmentJob(jobId, payload),
    onSuccess: (_data, { jobId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(jobId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not archive the job. Please try again.'));
    },
  });
}
